import React, { useState, useCallback, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  Cog6ToothIcon,
  UsersIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ArrowLeftOnRectangleIcon,
  HomeIcon,
  BuildingOfficeIcon,
  SunIcon,
  MoonIcon,
  ChatBubbleLeftEllipsisIcon,
  CircleStackIcon,
  DocumentMagnifyingGlassIcon,
  ScaleIcon,
  ChartPieIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import Tooltip from '../ui/Tooltip';

const adminNav = [
  { name: 'Dashboard', href: '/admin', icon: HomeIcon },
  { name: 'User Management', href: '/admin/users', icon: UsersIcon },
  { name: 'Organizations', href: '/admin/organizations', icon: BuildingOfficeIcon },
  { name: 'System Settings', href: '/admin/settings', icon: Cog6ToothIcon },
  { name: 'LLM Integration', href: '/admin/llm-integration', icon: AcademicCapIcon },
  { name: 'Analytics', href: '/admin/analytics', icon: ChartBarIcon },
  { name: 'LLM Query', href: '/admin/research/llm-query', icon: ChatBubbleLeftEllipsisIcon },
  { name: 'Legal DB Search', href: '/admin/research/legal-db-search', icon: CircleStackIcon },
  { name: 'Comprehensive Research', href: '/admin/research/comprehensive-research', icon: DocumentMagnifyingGlassIcon },
  { name: 'Case Recommendations', href: '/admin/research/case-recommendations', icon: ScaleIcon },
  { name: 'LLM Usage Dashboard', href: '/admin/analytics/llm-usage', icon: ChartPieIcon },
];

const ADMIN_SIDEBAR_COLLAPSED_KEY = 'easylaw_admin_sidebar_collapsed';

const isItemActive = (item, pathname) =>
  pathname === item.href ||
  (item.href !== '/admin' && pathname.startsWith(item.href.split('?')[0]) && item.href.split('?')[0] !== '/admin');

const AdminSidebarContent = ({ collapsed, onNavigate, onToggleCollapse, isMobile }) => {
  const location = useLocation();
  return (
    <div className="flex flex-col h-full">
      <div
        className={`flex items-center h-16 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 ${
          collapsed ? 'justify-center px-2' : 'justify-between px-4'
        }`}
      >
        <div className="flex items-center min-w-0">
          <Cog6ToothIcon className="h-8 w-8 text-primary-600 dark:text-primary-400 flex-shrink-0" />
          {!collapsed && (
            <span className="ml-2 text-xl font-semibold text-gray-900 dark:text-white truncate">
              Admin Panel
            </span>
          )}
        </div>
        {isMobile && (
          <button
            type="button"
            onClick={onNavigate}
            className="ml-2 p-1 rounded-md text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 md:hidden"
            aria-label="Close sidebar"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      <nav
        aria-label="Admin navigation"
        className={`flex-1 overflow-y-auto py-4 space-y-1 ${collapsed ? 'px-2' : 'px-3'}`}
      >
        {adminNav.map((item) => {
          const active = isItemActive(item, location.pathname);
          const linkClasses = `
            group flex items-center text-sm font-medium rounded-md transition-colors duration-150
            ${collapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'}
            ${active
              ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}
          `;
          const iconClasses = `
            h-5 w-5 transition-colors duration-150 flex-shrink-0
            ${collapsed ? '' : 'mr-3'}
            ${active
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'}
          `;

          const link = (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavigate}
              aria-current={active ? 'page' : undefined}
              aria-label={collapsed ? item.name : undefined}
              className={linkClasses}
            >
              <item.icon className={iconClasses} aria-hidden="true" />
              {!collapsed && <span className="truncate">{item.name}</span>}
            </Link>
          );

          return collapsed ? (
            <Tooltip key={item.name} content={item.name} position="right">
              {link}
            </Tooltip>
          ) : (
            link
          );
        })}
      </nav>

      <div className={`border-t border-gray-200 dark:border-gray-700 ${collapsed ? 'p-2' : 'p-4'} space-y-1`}>
        {onToggleCollapse && !isMobile && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className={`w-full group flex items-center text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 ${
              collapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'
            }`}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <ChevronDoubleRightIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            ) : (
              <>
                <ChevronDoubleLeftIcon className="mr-3 h-5 w-5 text-gray-500 dark:text-gray-400" />
                <span>Collapse</span>
              </>
            )}
          </button>
        )}
        <Link
          to="/"
          onClick={onNavigate}
          aria-label={collapsed ? 'Back to Main App' : undefined}
          className={`group flex items-center text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 ${
            collapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'
          }`}
        >
          <HomeIcon className={`h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0 ${collapsed ? '' : 'mr-3'}`} />
          {!collapsed && <span>Back to Main App</span>}
        </Link>
      </div>
    </div>
  );
};

const AdminLayout = () => {
  const location = useLocation();
  const { logout } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(ADMIN_SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(ADMIN_SIDEBAR_COLLAPSED_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  const closeMobileSidebar = useCallback(() => setSidebarOpen(false), []);

  // Keyboard shortcut: [ to toggle sidebar collapse on desktop
  useEffect(() => {
    const handler = (e) => {
      if (
        e.key === '[' &&
        !e.ctrlKey && !e.metaKey && !e.altKey &&
        !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) &&
        !e.target.isContentEditable
      ) {
        e.preventDefault();
        toggleSidebarCollapsed();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [toggleSidebarCollapsed]);

  // Close mobile drawer on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const currentPage =
    adminNav.find((item) => isItemActive(item, location.pathname))?.name || 'Admin';

  return (
    <div className="min-h-screen flex bg-gray-100 dark:bg-gray-900">
      {/* Desktop Sidebar - fixed */}
      <div
        className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 transition-all duration-300 ease-in-out ${
          sidebarCollapsed ? 'md:w-[4.5rem]' : 'md:w-64'
        }`}
        data-testid="admin-sidebar-desktop"
        data-collapsed={String(sidebarCollapsed)}
      >
        <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-gray-800 shadow-md">
          <AdminSidebarContent
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebarCollapsed}
            isMobile={false}
          />
        </div>
      </div>

      {/* Mobile Sidebar - off-canvas */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="admin-sidebar-mobile">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
            onClick={closeMobileSidebar}
            data-testid="admin-sidebar-overlay"
            aria-hidden="true"
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white dark:bg-gray-800 shadow-md animate-slide-in-left">
            <AdminSidebarContent
              collapsed={false}
              onNavigate={closeMobileSidebar}
              isMobile={true}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ease-in-out ${
          sidebarCollapsed ? 'md:pl-[4.5rem]' : 'md:pl-64'
        }`}
      >
        <header className="bg-white dark:bg-gray-800 shadow-sm h-16 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center min-w-0">
            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="md:hidden mr-2 p-2 rounded-md text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Open sidebar"
              data-testid="admin-mobile-menu-button"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
              {currentPage}
            </h1>
          </div>

          {/* Action buttons */}
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            <Tooltip
              content={darkMode ? 'Switch to light mode (Shift+D)' : 'Switch to dark mode (Shift+D)'}
              position="bottom"
            >
              <button
                type="button"
                className="rounded-full bg-white dark:bg-gray-700 p-1 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                onClick={toggleDarkMode}
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? (
                  <SunIcon className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <MoonIcon className="h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </Tooltip>

            <Tooltip content="Sign out" position="bottom">
              <button
                type="button"
                onClick={logout}
                className="rounded-full bg-white dark:bg-gray-700 p-1 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-label="Sign out"
              >
                <ArrowLeftOnRectangleIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </Tooltip>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
